import { toothUtils } from "@/features/tooth/lib/toothUtils";
import { utils } from "@/globals/utils";
import { mathUtils, Point } from "@/lib/math";
import Victor from "victor";


export class EspecificacaoBuilder {

    

    /**
     * 
     * @param {string | null} grupoEspecificacao 
     * @param {Array<string>} externalLocations 
     * @param {{faces: Array<string>, portions: Array<string>}} internalLocations 
     * @param {Array<string>} toothRegionList 
     * @param {string | null} closestToothId
     * @param {string | null} activeToothId
     * @param {Array<Object>} denteJobTools 
     * @param {Array<Object>} dentesProvaveisTools 
     * @param {{width: number, height: number}} mediaTamanhoDente 
     */
    constructor(
        grupoEspecificacao,
        externalLocations,
        internalLocations,
        toothRegionList,
        closestToothId,
        activeToothId,
        denteJobTools,
        dentesProvaveisTools,
        mediaTamanhoDente
    ) {
        /**
         * @type {string}
         */
        this.grupoEspecificacao = grupoEspecificacao;
        /**
         * @type {Array<string>}
         */
        this.externalLocations = externalLocations;
        /**
         * @type {{faces: Array<string>, portions: Array<string>}}
         */
        this.internalLocations = internalLocations;
        /**
         * @type {Array<string>}
         */
        this.toothRegionList = toothRegionList;
        /**
         * @type {string | null}
         */
        this.closestToothId = closestToothId;
        /**
         * @type {string | null}
         */
        this.activeToothId = activeToothId;
        /**
         * @type {Array<Object>}
         */
        this.denteJobTools = denteJobTools;
        /**
         * @type {Array<Object>}
         */
        this.dentesProvaveisTools = dentesProvaveisTools;
        /**
         * @type {{width: number, height: number}}
         */
        this.mediaTamanhoDente = mediaTamanhoDente;
    }

    /**
     * @returns {boolean}
     */
    get localizadoNaMandibula() {
        return this.externalLocations
            .some(regiao => ['lb_rRegAntMand', 'lb_rCorpoDirMand', 'lb_rCorpoEsqMand'].includes(regiao));
    }

    /**
     * @returns {boolean}
     */
    get localizadoNaMaxila() {
        return this.externalLocations
            .some(regiao => ['lb_rRegAntMax', 'lb_rRegPostDirMaxila', 'lb_rRegPostEsqMaxila', 'lb_rSeioMaxDir', 'lb_rSeioMaxEsq'].includes(regiao));
    }

    /**
     * @returns {boolean}
     */
    get localizadoDentroDaRaiz() {
        return (this.localizadoNaMandibula || this.localizadoNaMaxila)
    }

    get dentesProvaveisAusentesTools() {
        return this.dentesProvaveisTools.filter(tool => !this.denteJobTools.some(dente => dente.label.id == tool.label.id));
    }

    /**
     * @param {Point} referencePoint
     * @param {Point} clientPoint
     * @returns {{especificacao: 'regiao_dente' | 'provavel_regiao_dente', referenceToothIds: Array<string>} | null}
     */
    buildRegiaoDente(referencePoint, clientPoint) {

        let referenceToothId = this.activeToothId
        if (!this.activeToothId) {
            if (this.dentesProvaveisAusentesTools.length > 0) {
                const denteProvavelAtivo = utils.findDente(document, clientPoint, this.dentesProvaveisAusentesTools, undefined)
                if (denteProvavelAtivo) {
                    referenceToothId = denteProvavelAtivo.label.id

                    const detalhesDenteProvavel = toothUtils.getToothDetailsByLabelId(referenceToothId)
                    const dentesPresentesMesmaArcada = this.denteJobTools.filter(dente => {
                        const detalhes = toothUtils.getToothDetailsByLabelId(dente.label.id)
                        return detalhes.posicao == detalhesDenteProvavel.posicao
                    })
                    const dentePresenteMaisProximo = utils.findClosestToolByPosition(dentesPresentesMesmaArcada, referencePoint)
                    const muitoDistante = !dentePresenteMaisProximo?.tool || dentePresenteMaisProximo.distance > this.mediaTamanhoDente.width * 3
                    if (muitoDistante) {
                        return {
                            especificacao: this._mountEspecificacao('provavel_regiao_dente'),
                            referenceToothIds: [referenceToothId]
                        };
                    }
                }
            }

            if (!referenceToothId && this.toothRegionList.length > 0)
                referenceToothId = this.toothRegionList.at(0)
        }
        
        if (!referenceToothId)
            return null;

        return {
            especificacao: this._mountEspecificacao('regiao_dente'),
            referenceToothIds: [referenceToothId]
        };
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'proximo_apice_dente', referenceToothIds: Array<string>} | null}
     */
    buildProximoApiceDente(referencePoint) {
        if (this.toothRegionList.length < 1)
            return null;
        if (!this.localizadoDentroDaRaiz)
            return null;

        let dentesLaterais = this.toothRegionList
            .map(toothId => {
                const longAxisSegment = this._getToothLongAxisSegment(toothId)
                if (!longAxisSegment)
                    return null;
                const projecaoLongoEixo = mathUtils.getPointProjectionOnSegment(referencePoint, longAxisSegment)
                const depoisDaRaizDoDente = projecaoLongoEixo.factor > 1
                if (!depoisDaRaizDoDente)
                    return null;
                const distanciaDoPonto = projecaoLongoEixo.proj
                    .subtract(Victor.fromObject(referencePoint))
                    .length()

                return {
                    toothId: toothId,
                    length: distanciaDoPonto,
                    longAxisSegment
                };
            })
            .filter(item => item)
            .sort((a, b) => a.length - b.length)
            .map(item => {
                const ladoRelativoAoLongoEixo = mathUtils.pointSideRelatedToLineSegment(referencePoint, item.longAxisSegment[0], item.longAxisSegment[1])
                return {
                    side: ladoRelativoAoLongoEixo,
                    toothId: item.toothId
                }
            })

        dentesLaterais = dentesLaterais
            .filter(item => ['left', 'right'].includes(item.side))
            .reduce((acc, current) => {
                if (!acc.some(item => item.side == current.side))
                    acc.push(current)
                return acc
            }, [])

        if (!dentesLaterais.length)
            return null;

        let referenceToothIds = dentesLaterais.map(item => item.toothId)

        return {
            especificacao: this._mountEspecificacao('proximo_apice_dente'),
            referenceToothIds: referenceToothIds
        };
    }

    /**
     * @param {Point} referencePoint
     * @param {Point} clientPoint
     * @returns {{especificacao: 'localizado_raiz_dente' | 'localizado_provavel_raiz_dente', referenceToothIds: Array<string>} | null}
     */
    buildLocalizadoRaizDente(referencePoint, clientPoint) {
        if (!this.localizadoDentroDaRaiz)
            return null;

        let referenceToothId = this.activeToothId
        if (!referenceToothId) {
            // console.log('buildLocalizadoRaizDente', 'this.dentesProvaveisAusentesTools', this.dentesProvaveisAusentesTools.map(item => item.label.id).join(', '))
            if (!this.dentesProvaveisAusentesTools.length)
                return null;
            const denteProvavelAtivo = utils.findDente(document, clientPoint, this.dentesProvaveisAusentesTools, undefined)
            // console.log('buildLocalizadoRaizDente', { ...clientPoint }, 'denteProvavelAtivo', denteProvavelAtivo)
            if (!denteProvavelAtivo)
                return null;

            referenceToothId = denteProvavelAtivo.label.id
            // console.log('buildLocalizadoRaizDente', 'referenceToothId', referenceToothId)
            return {
                especificacao: this._mountEspecificacao('localizado_provavel_raiz_dente'),
                referenceToothIds: [referenceToothId]
            }
        }

        return {
            especificacao: this._mountEspecificacao('localizado_raiz_dente'),
            referenceToothIds: [referenceToothId]
        };
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'localizado_distal_dente' | 'localizado_distal_raiz_dente', referenceToothIds: Array<string>} | null}
     */
    buildLocalizadoDistalDente(referencePoint) {
        if (this.toothRegionList.length != 1)
            return null;
        const referenceToothId = this.toothRegionList.at(0)
        const detalhesDente = toothUtils.getToothDetailsByLabelId(referenceToothId);

        const availableToothNumbers = this.denteJobTools
            .map(tool => toothUtils.getToothDetailsByLabelId(tool.label.id)?.numero)
            .filter(numero => numero);

        const denteMaisDistante = toothUtils.getMostDistantToothNumberFromList(availableToothNumbers, detalhesDente.posicao, detalhesDente.lado)
        if (denteMaisDistante != detalhesDente.numero)
            return null;

        // Localizacoes internas para regiao de dente significa fora mas ainda proximo.
        const aDistalDoDente = this.internalLocations.faces.some(f => ['raiz__distal', 'coroa__distal'].includes(f))
        if (!aDistalDoDente)
            return null;

        const aDistalDaRaizDente = this.internalLocations.faces.some(f => ['raiz__distal'].includes(f))
        if (aDistalDaRaizDente && this.localizadoDentroDaRaiz) {
            return {
                especificacao: this._mountEspecificacao('localizado_distal_raiz_dente'),
                referenceToothIds: [referenceToothId]
            };
        }

        return {
            especificacao: this._mountEspecificacao('localizado_distal_dente'),
            referenceToothIds: [referenceToothId]
        };
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'localizado_entre_dentes' | 'localizado_entre_raizes_dentes', referenceToothIds: Array<string>} | null}
     */
    buildLocalizadoEntreDentes(referencePoint) {
        if (this.toothRegionList.length < 2)
            return null;
        let referenceToothIds = this.toothRegionList

        let dentesLaterais = this.toothRegionList
            .map(toothId => {
                const longAxisSegment = this._getToothLongAxisSegment(toothId)
                if (!longAxisSegment)
                    return null;
                const projecaoLongoEixo = mathUtils.getPointProjectionOnSegment(referencePoint, longAxisSegment)
                const lateralAoLongoEixo = projecaoLongoEixo.factor > 0 && projecaoLongoEixo.factor < 1
                if (!lateralAoLongoEixo)
                    return null;
                const distanciaDoPonto = projecaoLongoEixo.proj
                    .subtract(Victor.fromObject(referencePoint))
                    .length()


                return {
                    toothId: toothId,
                    length: distanciaDoPonto,
                    longAxisSegment
                };
            })
            .filter(item => item)
            .sort((a, b) => a.length - b.length)
            .map(item => {
                const ladoRelativoAoLongoEixo = mathUtils.pointSideRelatedToLineSegment(referencePoint, item.longAxisSegment[0], item.longAxisSegment[1])
                return {
                    side: ladoRelativoAoLongoEixo,
                    toothId: item.toothId
                }
            })

        dentesLaterais = dentesLaterais
            .filter(item => ['left', 'right'].includes(item.side))
            .reduce((acc, current) => {
                if (!acc.some(item => item.side == current.side))
                    acc.push(current)
                return acc
            }, [])

        // console.log('estaEntreOsDentes', dentesLaterais.map(item => item.side)
        // .sort().join('.'))
        // Se é entre, ele deve estar a direita de um e a esquerda de outro.
        const estaEntreOsDentes = dentesLaterais.map(item => item.side)
            .sort().join('.') == 'left.right'

        if (!estaEntreOsDentes)
            return null;

        referenceToothIds = dentesLaterais.map(item => item.toothId)

        if (referenceToothIds.length < 2)
            return null;

        const maisProximoDaRaiz = this.internalLocations.portions.some(p => p.startsWith('raiz__') || p.startsWith('dente_completo__raiz'))
        if (maisProximoDaRaiz && this.localizadoDentroDaRaiz) {
            return {
                especificacao: this._mountEspecificacao('localizado_entre_raizes_dentes'),
                referenceToothIds: referenceToothIds
            };
        }

        return {
            especificacao: this._mountEspecificacao('localizado_entre_dentes'),
            referenceToothIds: referenceToothIds
        };
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'localizado_entre_raizes_dente', referenceToothIds: Array<string>} | null}
     */
    builLocalizadoEntreRaizesDoDente(referencePoint) {
        if (this.toothRegionList.length < 1)
            return null;

        if (!this.localizadoDentroDaRaiz)
            return null;

        const inclusoNaRaizDoDente = this.internalLocations.portions.some(p => p.startsWith('raiz__') || p.startsWith('dente_completo__raiz'));
        if (!inclusoNaRaizDoDente)
            return null;

        let referenceToothId = this.activeToothId
        if (!this.activeToothId && this.toothRegionList.length == 1) {
            referenceToothId = this.toothRegionList.at(0)
            const longAxisSegment = this._getToothLongAxisSegment(referenceToothId)
            const contourSegment = this._getToothContourSegment(referenceToothId)
            if (!longAxisSegment || !contourSegment)
                return null;
            const projecaoContorno = mathUtils.getPointProjectionOnSegment(referencePoint, contourSegment)
            const projecaoLongoEixo = mathUtils.getPointProjectionOnSegment(referencePoint, longAxisSegment)
            const noMeioDoDente = (projecaoContorno.factor > 0.2 && projecaoContorno.factor < 0.8)
            const dentroDaRaizDente = (projecaoLongoEixo.factor >= 0.5 && projecaoLongoEixo.factor < 1)
            if (!(dentroDaRaizDente && noMeioDoDente))
                return null;
        }

        if (!referenceToothId)
            return null;

        return {
            especificacao: this._mountEspecificacao('localizado_entre_raizes_dente'),
            referenceToothIds: [referenceToothId]
        }
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'localizado_acima_dente', referenceToothIds: Array<string>} | null}
     */
    buildLocalizadoAcimaDente(referencePoint) {
        if (this.toothRegionList.length != 1)
            return null;
        if (!this.localizadoNaMaxila)
            return null;

        const referenceToothId = this.toothRegionList.at(0)
        const longAxisSegment = this._getToothLongAxisSegment(referenceToothId)
        if (!longAxisSegment)
            return null;

        // Se estiver acima do longo eixo do dente.
        // O longo eixo sendo uma linha coroa -> raiz;
        const projecaoLongoEixo = mathUtils.getPointProjectionOnSegment(referencePoint, longAxisSegment)
        if (projecaoLongoEixo.factor > 1)
            return { especificacao: this._mountEspecificacao('localizado_acima_dente'), referenceToothIds: [referenceToothId] };

        return null;
    }

    /**
     * @param {Point} referencePoint
     * @returns {{especificacao: 'localizado_abaixo_dente' | 'localizado_mandibula_abaixo_dente', referenceToothIds: Array<string>} | null}
     */
    buildLocalizadoAbaixoDente(referencePoint) {
        if (this.toothRegionList.length != 1 && !this.closestToothId)
            return null;
        if (!this.localizadoNaMandibula)
            return null;

        const referenceToothId = this.toothRegionList.at(0) || this.closestToothId

        const toothRegionTool = this.denteJobTools.find(tool => tool.label.id == referenceToothId);
        if (!toothRegionTool)
            return null;

        // console.log('toothRegionTool.coords.longAxisBaseMandibula', toothRegionTool.coords.longAxisBaseMandibula)

        // Segmento que vai do fim do longo eixo até bater no contorno da mandibula
        const longoEixoMandibulaSegment = toothRegionTool.coords.longAxisBaseMandibula
        if (!longoEixoMandibulaSegment)
            return null;

        const projecaoLongoEixo = mathUtils.getPointProjectionOnSegment(referencePoint, longoEixoMandibulaSegment)
        const dentroDoLongoEixoMandibula = projecaoLongoEixo.factor > 0 && projecaoLongoEixo.factor
        if (!dentroDoLongoEixoMandibula)
            return null;

        const maisProximoDaRaiz = projecaoLongoEixo.factor >= 0 && projecaoLongoEixo.factor < 0.5
        const maisProximoDaMandibula = projecaoLongoEixo.factor >= 0.5 && projecaoLongoEixo.factor < 1
        if (maisProximoDaRaiz)
            return { especificacao: this._mountEspecificacao('localizado_abaixo_dente'), referenceToothIds: [referenceToothId] }
        else if (maisProximoDaMandibula)
            return { especificacao: this._mountEspecificacao('localizado_mandibula_abaixo_dente'), referenceToothIds: [referenceToothId] }

        return null;
    }

    _mountEspecificacao(especificacao) {
        if (!this.grupoEspecificacao)
            return especificacao;
        return `${this.grupoEspecificacao}__${especificacao}`
    }

    _getToothLongAxisSegment(toothId) {
        const toothRegionTool = this.denteJobTools.find(tool => tool.label.id == toothId);
        if (!toothRegionTool)
            return null;

        const longAxisSegment = toothRegionTool.coords.longAxis
        if (!longAxisSegment || longAxisSegment.length != 2)
            return null

        return longAxisSegment
    }

    _getToothContourSegment(toothId) {
        const toothRegionTool = this.denteJobTools.find(tool => tool.label.id == toothId);
        if (!toothRegionTool)
            return null;

        const segment = toothRegionTool.coords.contourSegment
        if (!segment || segment.length != 2)
            return null

        return segment
    }
}